import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Server function to create a Stripe checkout session for direct offer purchase.
 * Only requires offerId since an offer always belongs to a coach.
 */
export const createCheckout = createServerFn({ method: "POST" })
  .validator(z.object({ offerId: z.string() }))
  .handler(async ({ data }) => {
    const { offerId } = data;
    const baseUrl = process.env.BASE_URL || "https://dreamcoachmatch.com";
    const { getDBWithInit } = await import("~/lib/db");

    // Get the offer with coach information
    const offer = await (
      await getDBWithInit()
    ).query.offers.findFirst({
      where: (offers, { eq }) => eq(offers.id, offerId),
      with: { coach: true },
    });

    if (!offer) {
      throw new Response("Offer not found", { status: 404 });
    }

    if (!offer.directSaleEnabled || !offer.price) {
      throw new Response("Offer not available for direct sale", {
        status: 400,
      });
    }

    const { getStripe } = await import("~/lib/stripe");
    const stripe = await getStripe();

    // Create checkout session for one-time payment
    const session = await stripe.checkout.sessions.create(
      {
        mode: "payment",
        line_items: [
          {
            price_data: {
              currency: offer.currency!.toLowerCase(),
              product_data: {
                name: offer.headline || "Coaching Offer",
                description: offer.description || undefined,
              },
              unit_amount: offer.price,
            },
            quantity: 1,
          },
        ],
        payment_intent_data: {
          application_fee_amount: Math.round(
            offer.price *
              parseFloat(process.env.VITE_APPLICATION_FEE_RATIO || "0.1"),
          ),
          metadata: {
            type: "direct_offer_purchase",
            coachId: offer.coach.id,
            offerId: offer.id,
          },
        },
        success_url: `${baseUrl}/${offer.coach.username}/offer?success=true`,
        cancel_url: `${baseUrl}/${offer.coach.username}/offer`,
        customer_creation: "always",
        invoice_creation: {
          enabled: true,
        },
        metadata: {
          type: "direct_offer_purchase",
          coachId: offer.coach.id,
          offerId: offer.id,
        },
      },
      { stripeAccount: offer.coach.stripeAccountId! },
    );

    return {
      success: true,
      checkoutUrl: session.url!,
    };
  });
